import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  SimpleGrid,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Heading,
} from '@chakra-ui/react';
import { Input } from 'reactstrap';
import { toast } from 'react-toastify';
import httpInjectorService from 'services/http-injector.service';
import { decryptData } from 'utils/crypto';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import { BulletList } from 'react-content-loader';
import { demoEmployeeWorkDetails } from 'components/demodata/Dummydata';

const Work = () => {
  const [formData, setFormData] = useState({
    employee_id: '',
    date_of_joining: '',
    probation_period: '',
    employee_type: '',
    work_location: '',
    designations: '',
    department_name: '',
    subdepartment: '',
  });

  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [isEditingWorkInfo, setIsEditingWorkInfo] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const roleId = decryptData(Cookies.get('role_id'));
  const { id } = useParams();

  /* fetch profile data by ID  */
  const getProfileDataById = async () => {
    try {
      const isDemoMode = localStorage.getItem("demoMode") === "true";

if (isDemoMode) {
  setFormData(demoEmployeeWorkDetails[0]);
  setOriginalData(demoEmployeeWorkDetails[0]);
  return;
}
      if (!id) throw new Error('User ID not found');
      const response = await httpInjectorService.getProfiledetails(id);
      if (response?.data?.length > 0) {
        const user = response.data[0];
        const data = {
          employee_id: user.employee_id || '',
          date_of_joining: user.date_of_joining?.split('T')[0] || '',
          probation_period: user.probation_period || '',
          employee_type: user.employee_type || '',
          work_location: user.work_location || '',
          designations: user.designations || '',
          department_name: user.department_name?.trim() || '',
          subdepartment: user.subdepartment || '',
        };
        setFormData(data);
        setOriginalData(data);
      } else {
        toast.error('User not found');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load user data');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saveData = async () => {
    try {

      setIsSaving(true);

      const updatedFields = Object.keys(formData).reduce((acc, key) => {
        if (formData[key] !== originalData[key]) acc[key] = formData[key];
        return acc;
      }, {});

      if (Object.keys(updatedFields).length === 0) {
        toast.info('No changes detected.');
        setIsEditingBasicInfo(false);
        setIsEditingWorkInfo(false);
        return;
      }

      const payload = { user_id: id, ...updatedFields };
      await httpInjectorService.updateprofiledetails(payload);
      toast.success('Profile updated successfully');
      setOriginalData(formData);
      setIsEditingBasicInfo(false);
      setIsEditingWorkInfo(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await httpInjectorService.getdepartment();
      if (response.status === 'success') setDepartments(response.data);
    } catch (err) {}
  };

  const fetchDesignations = async () => {
    try {
      const response = await httpInjectorService.getdesignation();
      if (response.status === 'success') setDesignations(response.data);
    } catch (error) {}
  };

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([getProfileDataById(), fetchDepartments(), fetchDesignations()]);
      setLoading(false);
    };
    loadAllData();
  }, []);

  useEffect(() => {
    if (!formData.department_name || departments.length === 0) return;
    const dept = departments.find((d) => d.department_name === formData.department_name);
    if (dept) setSubDepartments(dept.subdepartments);
  }, [formData.department_name, departments]);

  /* shared styles */
  const inputStyle = (editing) => ({
    border: editing ? '1px solid #e2e8f0' : 'none',
    width: '100%',
    backgroundColor: editing ? '#f7fafc' : '#fff',
    padding: '8px 10px',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'background 0.2s',
  });

  const SectionHeader = ({ title, isEditing, onEdit, onSave, onCancel, canEdit }) => (
    <Flex
      justify="space-between"
      align="center"
      mb={5}
      direction={{ base: 'column', md: 'row' }}
      gap={{ base: 3, md: 0 }}
    >
      <Flex align="center" gap={2}>
        <Box w="4px" h="20px" bg="purple.500" borderRadius="full" />
        <Heading size="md" mb={0}>{title}</Heading>
      </Flex>

      {canEdit && (
        <HStack spacing={2}>
          {isEditing ? (
            <>
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                borderRadius="full"
                px={4}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                isLoading={isSaving}
                colorScheme="purple"
                size="sm"
                borderRadius="full"
                px={5}
                onClick={onSave}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              colorScheme="purple"
              size="sm"
              borderRadius="full"
              px={5}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </HStack>
      )}
    </Flex>
  );

  /* render */
  if (loading) return <BulletList />;

  return (
    <VStack spacing={4} align="stretch">

      {/* ── Basic Info ── */}
      <Box
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        boxShadow="sm"
        borderRadius="lg"
        p={5}
      >
        <SectionHeader
          title="Basic Info"
          isEditing={isEditingBasicInfo}
          canEdit={roleId === 2}
          onEdit={() => setIsEditingBasicInfo(true)}
          onSave={() => { saveData(); setIsEditingBasicInfo(false); }}
          onCancel={() => { setFormData(originalData); setIsEditingBasicInfo(false); }}
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Employee ID
            </FormLabel>
            <input
              type="text"
              name="employee_id"
              value={formData.employee_id}
              onChange={handleChange}
              disabled={!isEditingBasicInfo}
              placeholder="Enter employee ID"
              style={inputStyle(isEditingBasicInfo)}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Date of Joining
            </FormLabel>
            <input
              type="date"
              name="date_of_joining"
              value={formData.date_of_joining}
              onChange={handleChange}
              disabled={!isEditingBasicInfo}
              style={inputStyle(isEditingBasicInfo)}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Probation Period (days)
            </FormLabel>
            <input
              type="number"
              name="probation_period"
              value={formData.probation_period}
              onChange={handleChange}
              disabled={!isEditingBasicInfo}
              placeholder="e.g. 90"
              style={inputStyle(isEditingBasicInfo)}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Employee Type
            </FormLabel>
            <select
              name="employee_type"
              value={formData.employee_type}
              onChange={handleChange}
              disabled={!isEditingBasicInfo}
              style={inputStyle(isEditingBasicInfo)}
            >
              <option value="">Select type</option>
              <option value="intern">Intern</option>
              <option value="full-time">Full-time</option>
            </select>
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Work Location
            </FormLabel>
            <input
              type="text"
              name="work_location"
              value={formData.work_location}
              onChange={handleChange}
              disabled={!isEditingBasicInfo}
              placeholder="Enter work location"
              style={inputStyle(isEditingBasicInfo)}
            />
          </FormControl>
        </SimpleGrid>
      </Box>

      {/* ── Work Info ── */}
      <Box
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        boxShadow="sm"
        borderRadius="lg"
        p={5}
      >
        <SectionHeader
          title="Work Info"
          isEditing={isEditingWorkInfo}
          canEdit={roleId === 2}
          onEdit={() => setIsEditingWorkInfo(true)}
          onSave={() => saveData(() => setIsEditingWorkInfo(false))}
          onCancel={() => { setFormData(originalData); setIsEditingWorkInfo(false); }}
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Department
            </FormLabel>
            <Input
              type="select"
              name="department_name"
              value={formData.department_name}
              disabled={!isEditingWorkInfo}
              onChange={(e) => {
                const selectedDept = departments.find(
                  (dept) => dept.department_name === e.target.value,
                );
                setFormData((prev) => ({
                  ...prev,
                  department_name: selectedDept ? selectedDept.department_name : '',
                  department_Id: selectedDept ? selectedDept.department_id : '',
                  subdepartment: '',
                }));
              }}
              style={inputStyle(isEditingWorkInfo)}
            >
              <option value="">Select department</option>
              {departments?.map((dept) => (
                <option key={dept.department_id} value={dept.department_name}>
                  {dept.department_name}
                </option>
              ))}
            </Input>
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Sub Department
            </FormLabel>
            <Input
              type="select"
              name="subdepartment"
              value={formData.subdepartment}
              disabled={!isEditingWorkInfo}
              onChange={(e) => {
                const selectedSubDept = subDepartments.find(
                  (sub) => sub.subdepartment === e.target.value,
                );
                setFormData((prev) => ({
                  ...prev,
                  subdepartment: selectedSubDept ? selectedSubDept.subdepartment : '',
                  subdepartment_Id: selectedSubDept ? selectedSubDept.subdepartment_id : '',
                }));
              }}
              style={inputStyle(isEditingWorkInfo)}
            >
              <option value="">Select subdepartment</option>
              {Array.isArray(subDepartments) && subDepartments.length > 0 ? (
                subDepartments.map((subDept) => (
                  <option key={subDept.subdepartment_id} value={subDept.subdepartment}>
                    {subDept.subdepartment}
                  </option>
                ))
              ) : (
                <option value="" disabled>No subdepartments available</option>
              )}
            </Input>
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
              Designation
            </FormLabel>
            <Input
              type="select"
              name="designations"
              value={formData.designations}
              disabled={!isEditingWorkInfo}
              onChange={(e) => {
                const selectedDesignation = designations.find(
                  (d) => d.designations === e.target.value,
                );
                setFormData((prev) => ({
                  ...prev,
                  designations: selectedDesignation ? selectedDesignation.designations : '',
                  designation_Id: selectedDesignation ? selectedDesignation.id : '',
                }));
              }}
              style={inputStyle(isEditingWorkInfo)}
            >
              <option value="">Select designation</option>
              {designations.map((d) => (
                <option key={d.id} value={d.designations}>
                  {d.designations}
                </option>
              ))}
            </Input>
          </FormControl>
        </SimpleGrid>
      </Box>

    </VStack>
  );
};

export default Work;